import { Controller, Control } from 'react-hook-form'

import { useTimezones } from '@/core/hooks/misc/useTimezones'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PhoneInput } from '@/shared/components/atoms/PhoneNumber'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { CountrySelector } from '@/shared/components/molecules/CountrySelector/CountrySelector'

interface PersonalInfoSubStepProps {
	control: Control<any>
	isLoading?: boolean
}

const ID_TYPES = [
	{ value: 'DNI', label: 'DNI' },
	{ value: 'Passport', label: 'Passport' },
	{ value: 'License', label: 'Driver License' },
	{ value: 'ID', label: 'National ID' },
	{ value: 'Other', label: 'Other' },
]

export const PersonalInfoSubStep: React.FC<PersonalInfoSubStepProps> = ({
	control,
	isLoading = false,
}) => {
	const { t } = useTranslation('zones')
	const { data: timezones = [] } = useTimezones()

	// Prepare timezone options
	const timezoneOptions = timezones.map((timezone) => ({
		value: timezone.value,
		label: timezone.label,
	}))

	return (
		<div className="space-y-6">
			{/* Location Information Section */}
			<div className="space-y-2">
				<h3 className="text-lg font-semibold">
					{t('create.locationForm.title')}
				</h3>
			</div>

			{/* Location Name */}
			<div>
				<Label className="mb-2 block">
					{t('create.locationForm.fields.name.label')}
				</Label>
				<Controller
					name="store.name"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<div>
							<Input
								{...field}
								value={field.value || ''}
								placeholder={t('create.locationForm.fields.name.placeholder')}
								disabled={isLoading}
								className="h-10 text-sm"
							/>
							{error && <ErrorMessage error={error.message ?? ''} />}
						</div>
					)}
				/>
			</div>

			{/* Country and Address */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.country.label')}
					</Label>
					<Controller
						name="store.country"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<CountrySelector
									value={field.value as any}
									onChange={field.onChange}
									placeholder={t(
										'create.locationForm.fields.country.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.address.label')}
					</Label>
					<Controller
						name="store.address"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									placeholder={t(
										'create.locationForm.fields.address.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			</div>

			{/* City and ZIP Code */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.city.label')}
					</Label>
					<Controller
						name="store.city"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									placeholder={t('create.locationForm.fields.city.placeholder')}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.zipCode.label')}
					</Label>
					<Controller
						name="store.zip_code"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									placeholder={t(
										'create.locationForm.fields.zipCode.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			</div>

			{/* Timezone */}
			<div>
				<Label className="mb-2 block">
					{t('create.locationForm.fields.timezone.label')}
				</Label>
				<Controller
					name="store.timezone"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<div>
							<Select
								value={field.value || ''}
								onValueChange={field.onChange}
								disabled={isLoading}
							>
								<SelectTrigger className="h-10 w-full text-sm">
									<SelectValue
										placeholder={t(
											'create.locationForm.fields.timezone.placeholder'
										)}
									/>
								</SelectTrigger>
								<SelectContent>
									{timezoneOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{error && <ErrorMessage error={error.message ?? ''} />}
						</div>
					)}
				/>
			</div>

			{/* Manager Information Section */}
			<div className="space-y-2 pt-4">
				<h3 className="text-lg font-semibold">
					{t('create.locationForm.managerTitle')}
				</h3>
				<p className="text-sm text-muted-foreground">
					{t('create.locationForm.managerDescription')}
				</p>
			</div>

			{/* Manager Name */}
			<div>
				<Label className="mb-2 block">
					{t('create.locationForm.fields.managerName.label')}
				</Label>
				<Controller
					name="store.manager"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<div>
							<Input
								{...field}
								value={field.value || ''}
								placeholder={t(
									'create.locationForm.fields.managerName.placeholder'
								)}
								disabled={isLoading}
								className="h-10 text-sm"
							/>
							{error && <ErrorMessage error={error.message ?? ''} />}
						</div>
					)}
				/>
			</div>

			{/* ID Type and Number */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.idType.label')}
					</Label>
					<Controller
						name="store.id_type"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Select
									value={field.value || ''}
									onValueChange={field.onChange}
									disabled={isLoading}
								>
									<SelectTrigger className="h-10 w-full text-sm">
										<SelectValue
											placeholder={t(
												'create.locationForm.fields.idType.placeholder'
											)}
										/>
									</SelectTrigger>
									<SelectContent>
										{ID_TYPES.map((type) => (
											<SelectItem key={type.value} value={type.value}>
												{type.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.idNumber.label')}
					</Label>
					<Controller
						name="store.id_number"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									placeholder={t(
										'create.locationForm.fields.idNumber.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			</div>

			{/* Email and Phone Number */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.email.label')}
					</Label>
					<Controller
						name="store.email"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									type="email"
									value={field.value || ''}
									placeholder={t(
										'create.locationForm.fields.email.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="mb-2 block">
						{t('create.locationForm.fields.phone.label')}
					</Label>
					<Controller
						name="store.phone_number"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<div>
								<PhoneInput
									value={field.value || ''}
									onChange={field.onChange}
									placeholder={t(
										'create.locationForm.fields.phone.placeholder'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			</div>

			{/* Additional Information */}
			<div>
				<Label className="mb-2 block">
					{t('create.locationForm.fields.additionalInfo.label')}
				</Label>
				<Controller
					name="store.additional_information"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<div>
							<Textarea
								{...field}
								value={field.value || ''}
								placeholder={t(
									'create.locationForm.fields.additionalInfo.placeholder'
								)}
								disabled={isLoading}
								rows={3}
								className="text-sm bg-background border-border text-foreground resize-none"
							/>
							{error && <ErrorMessage error={error.message ?? ''} />}
						</div>
					)}
				/>
			</div>
		</div>
	)
}
